// Use direct fetch to bypass Supabase client locking during auth
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Extract Azure AD tenant ID from Supabase user object
 * @param {Object} user - Supabase user object
 * @returns {string|null} - Tenant ID or null if not found
 */
export function extractTenantId(user) {
  if (!user) return null;

  const azureIdentity = user.identities?.find(
    identity => identity.provider === 'azure'
  );

  // Azure AD stores tenant ID in identity_data.custom_claims.tid
  if (azureIdentity?.identity_data?.custom_claims?.tid) {
    return azureIdentity.identity_data.custom_claims.tid;
  }

  // Fallback: check user_metadata.custom_claims
  if (user.user_metadata?.custom_claims?.tid) {
    return user.user_metadata.custom_claims.tid;
  }

  return null;
}

/**
 * Check if a tenant ID is in the allowlist
 * Uses direct fetch to avoid Supabase client lock contention during auth
 * @param {string} tenantId - Azure AD tenant ID
 * @returns {Promise<{allowed: boolean, tenantName: string|null}>}
 */
export async function isTenantAllowed(tenantId) {
  if (!tenantId) {
    return { allowed: false, tenantName: null };
  }

  try {
    const url = `${supabaseUrl}/rest/v1/allowed_tenants?tenant_id=eq.${tenantId}&select=tenant_name`;

    const response = await fetch(url, {
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
    });

    if (!response.ok) {
      console.error('Tenant check failed:', response.status);
      return { allowed: false, tenantName: null };
    }

    const data = await response.json();

    if (!data || data.length === 0) {
      return { allowed: false, tenantName: null };
    }

    return { allowed: true, tenantName: data[0].tenant_name };
  } catch (err) {
    console.error('Tenant check error:', err);
    // On error, allow through to prevent blocking users
    return { allowed: true, tenantName: null };
  }
}

/**
 * Validate user's tenant and return validation result
 * @param {Object} user - Supabase user object
 * @returns {Promise<{valid: boolean, tenantId: string|null, tenantName: string|null, isAzureUser: boolean}>}
 */
export async function validateUserTenant(user) {
  if (!user) {
    return { valid: false, tenantId: null, tenantName: null, isAzureUser: false };
  }

  // Check if user authenticated via Azure
  const azureIdentity = user.identities?.find(
    identity => identity.provider === 'azure'
  );

  if (!azureIdentity) {
    // Not an Azure user (e.g., email/password) - allow through
    // Tenant validation only applies to Azure SSO users
    return { valid: true, tenantId: null, tenantName: null, isAzureUser: false };
  }

  const tenantId = extractTenantId(user);

  if (!tenantId) {
    console.error('Azure user but no tenant ID found in identity data');
    return { valid: false, tenantId: null, tenantName: null, isAzureUser: true };
  }

  const { allowed, tenantName } = await isTenantAllowed(tenantId);

  return {
    valid: allowed,
    tenantId,
    tenantName,
    isAzureUser: true
  };
}
